import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/lib/api';
const PUBLIC_URL = process.env.PUBLIC_URL || '';
const ThemeContext = createContext(null);
const DEFAULT_BACKGROUND_URL = `${PUBLIC_URL}/wardrobe-bg.jpg`;

const buildBackgroundCss = (imageUrl) => `
    background-image: url('${imageUrl}') !important;
    background-size: cover !important;
    background-position: center top !important;
    background-repeat: no-repeat !important;
    background-attachment: fixed !important;
`;

const applyDefaultBackground = () => {
  document.body.style.backgroundImage = `url('${DEFAULT_BACKGROUND_URL}')`;
  document.body.style.backgroundSize = 'cover';
  document.body.style.backgroundPosition = 'center top';
  document.body.style.backgroundRepeat = 'no-repeat';
  document.body.style.backgroundAttachment = 'fixed';
};

const resolveThemeImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('/api/')) return `${API_URL}${imageUrl}`;
  if (imageUrl.startsWith('/')) return `${PUBLIC_URL}${imageUrl}`;
  return imageUrl;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/** Convert hex color to HSL string (e.g. "270 25% 6%") for CSS custom properties */
function hexToHSL(hex) {
  if (!hex) return null;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Generate derived CSS variables from 6 core colors */
function buildCSSVars(colors) {
  if (!colors || !colors.primary) return '';
  const vars = {};
  const bg = hexToHSL(colors.background);
  const fg = hexToHSL(colors.foreground);
  const card = hexToHSL(colors.card);
  const primary = hexToHSL(colors.primary);
  const muted = hexToHSL(colors.muted);
  const border = hexToHSL(colors.border);
  if (bg) { vars['--background'] = bg; }
  if (fg) { vars['--foreground'] = fg; vars['--card-foreground'] = fg; vars['--popover-foreground'] = fg; vars['--secondary-foreground'] = fg; }
  if (card) { vars['--card'] = card; vars['--popover'] = card; vars['--secondary'] = card; }
  if (primary) { vars['--primary'] = primary; vars['--ring'] = primary; vars['--accent'] = primary; }
  vars['--primary-foreground'] = '0 0% 100%';
  vars['--accent-foreground'] = '0 0% 100%';
  if (muted) { vars['--muted'] = muted; vars['--muted-foreground'] = muted; }
  if (border) { vars['--border'] = border; vars['--input'] = border; }
  return Object.entries(vars).map(([k, v]) => `${k}: ${v};`).join('\n    ');
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'auto');
  const [colorTheme, setColorThemeState] = useState('default');
  const [resolvedTheme, setResolvedTheme] = useState('light');
  const [availableThemes, setAvailableThemes] = useState([]);

  // Fetch themes from API
  const fetchThemes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/themes`);
      setAvailableThemes(res.data.themes || []);
    } catch {
      // Fallback: empty — default theme still works
    }
  }, []);

  useEffect(() => { fetchThemes(); }, [fetchThemes]);

  // Light/dark toggle
  useEffect(() => {
    const root = document.documentElement;
    const applyTheme = (isDark) => {
      if (isDark) { root.classList.add('dark'); setResolvedTheme('dark'); }
      else { root.classList.remove('dark'); setResolvedTheme('light'); }
    };
    if (theme === 'auto') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mq.matches);
      const handler = (e) => applyTheme(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      applyTheme(theme === 'dark');
    }
  }, [theme]);

  // Inject dynamic CSS for the selected theme
  useEffect(() => {
    const STYLE_ID = 'dynamic-theme-css';
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }

    const requestedThemeId = !colorTheme || colorTheme === 'default' ? 'theme_default' : colorTheme;

    // Find the theme data
    const themeData = availableThemes.find(t => t.theme_id === requestedThemeId);
    if (!themeData) {
      styleEl.textContent = '';
      document.documentElement.classList.remove('dynamic-theme');
      applyDefaultBackground();
      return;
    }

    // Build CSS for dark and light variants
    const darkVars = buildCSSVars(themeData.dark_colors);
    const lightVars = buildCSSVars(themeData.light_colors);

    let css = '';
    if (darkVars) {
      css += `html.dark.dynamic-theme {\n    ${darkVars}\n}\n`;
    }
    if (lightVars) {
      css += `html:not(.dark).dynamic-theme {\n    ${lightVars}\n}\n`;
    }

    // Background images
    const darkImageUrl = resolveThemeImageUrl(themeData.dark_image) || DEFAULT_BACKGROUND_URL;
    const lightImageUrl = resolveThemeImageUrl(themeData.light_image) || DEFAULT_BACKGROUND_URL;

    if (darkImageUrl) {
      css += `html.dark.dynamic-theme body {\n${buildBackgroundCss(darkImageUrl)}\n}\n`;
    }
    if (lightImageUrl) {
      css += `html:not(.dark).dynamic-theme body {\n${buildBackgroundCss(lightImageUrl)}\n}\n`;
    }

    styleEl.textContent = css;

    // Toggle the dynamic-theme class
    document.documentElement.classList.add('dynamic-theme');

    return () => {
      document.documentElement.classList.remove('dynamic-theme');
    };
  }, [colorTheme, availableThemes, resolvedTheme]);

  const setThemePreference = (newTheme) => {
    localStorage.setItem('theme', newTheme);
    setTheme(newTheme);
  };

  const setColorTheme = (newColorTheme) => {
    setColorThemeState(newColorTheme || 'default');
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme: setThemePreference, colorTheme, setColorTheme, availableThemes, fetchThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};
